import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dialog from 'components/ReusableComponents/Dialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import hotelSetupApi from 'services/api/hotelSetup'

import styles from './CheckACHStatus.module.scss'

const RETRY_LIMIT = 5
const RETRY_DELAY = 4000

const CheckACHStatus = () => {
  const { addToast } = useToasts()

  const [isErred, setIsErred] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [retryCount, setRetryCount] = useState(0)
  const [isTryingPayment, setIsTryingPayment] = useState(false)
  const timeoutRef = useRef(null)

  const checkAchStatus = useCallback(async () => {
    setIsTryingPayment(true)
    try {
      const response = await hotelSetupApi.checkAchAuthForm()

      if (response === 'pending') {
        if (retryCount < RETRY_LIMIT) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }

          timeoutRef.current = setTimeout(() => {
            setRetryCount(prevCount => prevCount + 1)
          }, RETRY_DELAY)
        } else {
          setErrorText('Could not process the payment. Please try again later.')
          setIsErred(true)
        }
      } else if (response === 'failed') {
        setErrorText('Your payment was not successful. Please try again.')
        setIsErred(true)
      } else {
        setIsSuccessful(true)
      }
    } catch (err) {
      if (err.message.includes('Subsidiary not found')) {
        setErrorText('Subsidiary not found.')
      } else if (err.message.includes('Setup is not completed yet')) {
        setErrorText('Setup is not completed yet.')
      } else if (err.message.includes('ACH contract not found')) {
        setErrorText('ACH contract not found.')
      } else if (err.message.includes('Error while retrieving ACH contract')) {
        setErrorText('Error while retrieving ACH contract.')
      } else if (err.message.includes('ACH contract data is corrupted')) {
        setErrorText('ACH contract data is corrupted.')
      } else if (err.message.includes('Failed to process check ACH contract')) {
        setErrorText('ACH contract data is corrupted.')
      } else {
        setErrorText('Something went wrong.')
      }
      setIsErred(true)
    } finally {
      setIsTryingPayment(false)
    }
  }, [retryCount])

  useEffect(() => {
    if (!isSuccessful && !isErred) {
      checkAchStatus()
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [checkAchStatus, isSuccessful, isErred, retryCount])

  useEffect(() => {
    if (isSuccessful) {
      addToast('ACH payment was successful', {
        appearance: 'success'
      })
    }
  }, [addToast, isSuccessful])

  if (isErred) {
    return (
      <ConfirmationDialog
        title="Your payment was not successful"
        positiveMessage="Go back to payment"
        hasCloseButton={false}
        confirmLink="/hotel-setup/"
      >
        <p>
          {errorText} If you think this is a mistake, please contact us at{' '}
          <a target="_blank" href="mailto:billing@directful.com" rel="noreferrer">
            billing@directful.com
          </a>
          .
        </p>
      </ConfirmationDialog>
    )
  }

  if (isSuccessful) {
    return (
      <ConfirmationDialog
        title="Your payment was successful"
        positiveMessage="Start using the Directful Dashboard"
        hasCloseButton={false}
        onConfirm={() => {
          window.location.reload()
        }}
      >
        <p>Thank you for your payment. Hotel setup is now complete.</p>
      </ConfirmationDialog>
    )
  }

  if (isTryingPayment) {
    return (
      <Dialog>
        <div className={styles.container}>
          <LoadingIndicator inline longWaitingTreshold={999} />
          <p className={styles.text}>Please wait while we confirm your ACH details.</p>
        </div>
      </Dialog>
    )
  }

  return null
}

export default CheckACHStatus
